/**
 * Time parsing for the gateway AI analytics tools — direct port of the
 * editor's equivalent so the same prompt instructions work on both
 * surfaces. Accepts:
 *   - "now"                  → current time
 *   - "now-5m" / "now-2h"    → relative offset (s, m, h, d, w)
 *   - "2026-04-23T14:00:00Z" → absolute RFC3339
 * and returns an RFC3339 UTC string. Throws on unparseable input so the
 * tool layer can surface a structured error.
 */

const OFFSET_UNITS = {
  s: 1000,
  m: 60 * 1000,
  h: 60 * 60 * 1000,
  d: 24 * 60 * 60 * 1000,
  w: 7 * 24 * 60 * 60 * 1000,
};

export function resolveTimeShortcut(input, nowRef = new Date()) {
  if (typeof input !== "string" || !input.trim()) {
    throw new Error(`timeParse: expected string, got ${typeof input}`);
  }
  const trimmed = input.trim();
  if (trimmed === "now") return nowRef.toISOString();
  const relMatch = /^now\s*-\s*(\d+)\s*([smhdw])$/i.exec(trimmed);
  if (relMatch) {
    const amount = Number(relMatch[1]);
    const unit = relMatch[2].toLowerCase();
    if (amount <= 0) {
      throw new Error(
        `timeParse: offset must be positive (got "${trimmed}"). Use "now" for the current moment.`,
      );
    }
    const ms = amount * OFFSET_UNITS[unit];
    if (!Number.isFinite(ms)) throw new Error(`timeParse: bad offset "${trimmed}"`);
    return new Date(nowRef.getTime() - ms).toISOString();
  }
  const absDate = new Date(trimmed);
  if (!Number.isFinite(absDate.getTime())) {
    throw new Error(
      `timeParse: "${input}" is not a recognized time. Use RFC3339 (e.g. "2026-04-23T14:00:00Z") or a "now-Xm|h|d" shortcut.`,
    );
  }
  return absDate.toISOString();
}

export function resolveTimeRange(start, end = "now", nowRef = new Date()) {
  const startIso = resolveTimeShortcut(start, nowRef);
  const endIso = resolveTimeShortcut(end, nowRef);
  if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
    throw new Error(`timeParse: end (${end}) must be after start (${start}).`);
  }
  return { start: startIso, end: endIso };
}
